import {
  toCamelCase,
  WSButton,
  WSButtons,
  WSElementProps,
  WSFormOld,
  WSRadioInputGroup,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import React from "react";
import * as Yup from "yup";
import { ErrorAlert } from "../../../../v3/components/ErrorAlert";
import {
  AttributeDataType,
  CustomFieldResourceType
} from "@wingspanhq/wingspan-api-sdk";
import { queriesCustomFields } from "@query";

type Props = WSElementProps & {
  resourceType: CustomFieldResourceType;
  onClose(result: boolean): void;
};

export const AddCustomFieldModal: React.FC<Props> = ({
  resourceType,
  onClose
}) => {
  const [createCustomField, createCustomFieldMeta] =
    queriesCustomFields.createCustomField.useMutation();

  const queryCustomFieldList = queriesCustomFields.listCustomFields.useQueryAll(
    { filter: { resourceType: { eq: resourceType } } }
  );

  return (
    <WSFormOld
      defaultValues={{
        name: "",
        fieldType: AttributeDataType.STRING,
        required: "yes"
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string()
          .test(
            "name",
            "A custom field with this name already exists",
            name => {
              const key = toCamelCase(name ?? "");
              const existingCustomFields = queryCustomFieldList.flatData.map(
                customField => customField.key?.toLowerCase()
              );
              return !existingCustomFields.includes(key.toLowerCase());
            }
          )
          .required("Name is required"),
        fieldType: Yup.string(),
        required: Yup.string()
      })}
      onSubmit={async formData => {
        await createCustomField(
          {
            customFieldCreate: {
              name: formData.name,
              isRequired: formData.required === "yes",
              dataType: formData.fieldType,
              key: toCamelCase(formData.name),
              resourceType
            }
          },
          {
            onSuccess() {
              onClose(true);
            }
          }
        );
      }}
    >
      <WSFormOld.Field
        mb="XL"
        label="Field name"
        name="name"
        component={WSTextInput}
      />

      <WSFormOld.Field
        mb="2XL"
        label=" "
        name="required"
        component={WSRadioInputGroup}
        componentProps={{
          options: [
            {
              label: "Required",
              value: "yes"
            },
            {
              label: "Optional",
              value: "no"
            }
          ]
        }}
      />

      <WSFormOld.Field
        mb="XL"
        label="Field type"
        name="fieldType"
        component={WSRadioInputGroup}
        componentProps={{
          options: [
            {
              label: "Text field",
              value: AttributeDataType.STRING
            },
            {
              label: "Checkbox",
              value: AttributeDataType.BOOLEAN
            }
          ]
        }}
      />

      <ErrorAlert mt="XL" error={createCustomFieldMeta.error} context="Other" />

      <WSButtons mt="3XL" forceFullWidth>
        <WSFormOld.SubmitButton
          data-testid="submit"
          loading={createCustomFieldMeta.isLoading}
        >
          Create field
        </WSFormOld.SubmitButton>
        <WSButton.Tertiary onClick={() => onClose(false)}>
          Cancel
        </WSButton.Tertiary>
      </WSButtons>
    </WSFormOld>
  );
};
